<template>
  <div class="entity-list">
    <!-- Management Controls -->
    <div class="management-controls">
      <div class="search-filter-row">
        <div class="search-box">
          <i class="fas fa-search"></i>
          <input 
            type="text" 
            v-model="searchQuery" 
            :placeholder="`Search ${entityTerm.plural.toLowerCase()}...`"
            @input="handleSearch"
            class="search-input">
        </div>
        
        <select v-model="statusFilter" @change="applyFilters" class="filter-select">
          <option value="">All Statuses</option>
          <option value="COMPLIANT">Compliant</option>
          <option value="PARTIALLY_COMPLIANT">Partially Compliant</option>
          <option value="NON_COMPLIANT">Non-Compliant</option>
          <option value="ACTION_NEEDED">Action Needed</option>
          <option value="PENDING">Pending</option>
          <option value="ARCHIVED">Archived</option>
        </select>

        <select v-model="continuitySort" class="filter-select" title="Sort by coverage history">
          <option value="">Sort: Default</option>
          <option value="pct_desc">Coverage: High → Low</option>
          <option value="pct_asc">Coverage: Low → High</option>
          <option value="lapses_desc">Most lapses first</option>
        </select>
      </div>
      
      <div class="actions-row">
        <button @click="$emit('add-entity')" class="btn btn-primary">
          <i class="fas fa-plus"></i> Add {{ entityTerm.singular }}
        </button>
      </div>
    </div>
    
    <!-- Loading State -->
    <div v-if="loading" class="loading-state">
      <div class="skeleton-card" v-for="n in 5" :key="n">
        <div class="skeleton-line"></div>
        <div class="skeleton-line short"></div>
        <div class="skeleton-line"></div>
      </div>
    </div>
    
    <!-- Empty State -->
    <div v-else-if="entities.length === 0 && !searchQuery && !statusFilter" class="empty-state">
      <div class="empty-icon">
        <i class="fas fa-users"></i>
      </div>
      <h3>No {{ entityTerm.plural }} Yet</h3>
      <p>Get started by adding your first {{ entityTerm.singular.toLowerCase() }} to track compliance</p>
      <button @click="$emit('add-entity')" class="btn btn-primary">
        <i class="fas fa-plus"></i> Add First {{ entityTerm.singular }}
      </button>
    </div>
    
    <!-- No Results State -->
    <div v-else-if="entities.length === 0" class="empty-state">
      <div class="empty-icon">
        <i class="fas fa-search"></i>
      </div>
      <h3>No Results Found</h3>
      <p>No {{ entityTerm.plural.toLowerCase() }} match your search or filter</p>
      <button @click="clearFilters" class="btn btn-outline">
        Clear Filters
      </button>
    </div>
    
    <!-- Entity Cards -->
    <div v-else class="entity-cards">
      <EntityCard
        v-for="entity in displayEntities"
        :key="entity.id"
        :ref="el => { if (entity.id === highlightedSubjectId) highlightedCardRef = el }"
        :entity="entity"
        :entity-term="entityTerm"
        :location-term="locationTerm"
        :is-highlighted="entity.id === highlightedSubjectId"
        @view-requirements="$emit('view-requirements', $event)"
        @add-store="$emit('add-store', $event)"
        @upload-document="$emit('upload-document', $event)"
        @request-documents="$emit('request-documents', $event)"
        @request-from-agent="$emit('request-from-agent', $event)"
        @view-analysis="$emit('view-analysis', $event)"
        @archive-entity="$emit('archive-entity', $event)"
        @entity-updated="$emit('entity-updated', $event)"
      />
    </div>
    
    <!-- Pagination -->
    <div v-if="totalCount > pageSize" class="pagination-controls">
      <div class="pagination-info">
        Showing {{ startIndex }}-{{ endIndex }} of {{ totalCount }} {{ entityTerm.plural.toLowerCase() }}
      </div>
      
      <div class="pagination-buttons">
        <button 
          @click="previousPage" 
          :disabled="currentPage === 1"
          class="btn btn-outline btn-sm">
          <i class="fas fa-chevron-left"></i> Previous
        </button>
        
        <span class="page-numbers">
          <button
            v-for="page in visiblePages"
            :key="page"
            @click="goToPage(page)"
            :class="['page-btn', { active: page === currentPage }]">
            {{ page }}
          </button>
        </span>
        
        <button 
          @click="nextPage" 
          :disabled="currentPage >= totalPages"
          class="btn btn-outline btn-sm">
          Next <i class="fas fa-chevron-right"></i>
        </button>
      </div>
      
      <div class="page-size-controls">
          <label>
            Show:
            <select :value="pageSize" @change="handlePageSizeChange" class="form-select">
              <option :value="20">20</option>
              <option :value="50">50</option>
              <option :value="100">100</option>
            </select>
            per page
          </label>
      </div>
    </div>
  </div>
</template>

<script>
import EntityCard from './EntityCard.vue';
import { debounce } from 'lodash-es';

export default {
  name: 'EntityList',
  components: {
    EntityCard
  },
  props: {
    entities: {
      type: Array,
      required: true
    },
    loading: {
      type: Boolean,
      default: false
    },
    totalCount: {
      type: Number,
      default: 0
    },
    currentPage: {
      type: Number,
      default: 1
    },
    pageSize: {
      type: Number,
      default: 20
    },
    entityTerm: {
      type: Object,
      default: () => ({ singular: 'Company', plural: 'Companies' })
    },
    locationTerm: {
      type: Object,
      default: () => ({ singular: 'Location', plural: 'Locations' })
    },
    highlightedSubjectId: {
      type: Number,
      default: null
    }
  },
  data() {
    return {
      searchQuery: '',
      statusFilter: '',
      continuitySort: '',
      debouncedSearch: null,
      highlightedCardRef: null
    };
  },
  watch: {
    highlightedSubjectId: {
      handler(newId) {
        if (newId) {
          // Wait for DOM to update, then scroll to the highlighted card
          this.$nextTick(() => {
            setTimeout(() => {
              if (this.highlightedCardRef && this.highlightedCardRef.$el) {
                this.highlightedCardRef.$el.scrollIntoView({ 
                  behavior: 'smooth', 
                  block: 'center' 
                });
              }
            }, 100);
          });
        }
      },
      immediate: true
    }
  },
  computed: {
    displayEntities() {
      const list = [...this.entities];
      if (!this.continuitySort) return list;

      const pct = (entity) => {
        const summary = entity.continuity_summary;
        if (!summary?.has_interval_data) return -1;
        return summary.pct_covered ?? -1;
      };
      const lapses = (entity) => entity.continuity_summary?.lapse_count ?? 0;

      if (this.continuitySort === 'pct_desc') {
        return list.sort((a, b) => pct(b) - pct(a));
      }
      if (this.continuitySort === 'pct_asc') {
        return list.sort((a, b) => {
          const av = pct(a);
          const bv = pct(b);
          if (av === -1 && bv === -1) return 0;
          if (av === -1) return 1;
          if (bv === -1) return -1;
          return av - bv;
        });
      }
      if (this.continuitySort === 'lapses_desc') {
        return list.sort((a, b) => lapses(b) - lapses(a));
      }
      return list;
    },
    totalPages() {
      return Math.ceil(this.totalCount / this.pageSize);
    },
    startIndex() {
      return (this.currentPage - 1) * this.pageSize + 1;
    },
    endIndex() {
      return Math.min(this.currentPage * this.pageSize, this.totalCount);
    },
    visiblePages() {
      const pages = [];
      const maxVisible = 5;
      const current = this.currentPage;
      const total = this.totalPages;
      
      let start = Math.max(1, current - Math.floor(maxVisible / 2));
      let end = Math.min(total, start + maxVisible - 1);
      
      if (end - start + 1 < maxVisible) {
        start = Math.max(1, end - maxVisible + 1);
      }
      
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      
      return pages;
    }
  },
  created() {
    this.debouncedSearch = debounce(this.performSearch, 300);
  },
  methods: {
    applyContinuitySort(value) {
      this.continuitySort = value || '';
    },

    handleSearch() {
      this.debouncedSearch();
    },
    
    performSearch() {
      this.$emit('search', this.searchQuery);
    },
    
    applyFilters() {
      this.$emit('filter', this.statusFilter);
    },
    
    clearFilters() {
      this.searchQuery = '';
      this.statusFilter = '';
      this.$emit('search', '');
      this.$emit('filter', '');
    },
    
    previousPage() {
      if (this.currentPage > 1) {
        this.$emit('page-change', this.currentPage - 1);
      }
    },
    
    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.$emit('page-change', this.currentPage + 1);
      }
    },
    
    goToPage(page) {
      this.$emit('page-change', page);
    },
    
    handlePageSizeChange(event) {
      this.$emit('page-size-change', parseInt(event.target.value));
    }
  }
};
</script>

<style scoped>
.entity-list {
  margin-top: 0.75rem;
}

.management-controls {
  background: white;
  padding: 0.85rem 1rem;
  border-radius: 8px;
  margin-bottom: 0.75rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.search-filter-row {
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;
}

.search-box {
  flex: 1;
  position: relative;
}

.search-box i {
  position: absolute;
  left: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: #94a3b8;
}

.search-input {
  width: 100%;
  padding: 0.75rem 1rem 0.75rem 2.75rem;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 0.9375rem;
}

.search-input:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.filter-select {
  padding: 0.75rem 1rem;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 0.9375rem;
  min-width: 200px;
}

.filter-select:focus {
  outline: none;
  border-color: #667eea;
}

.actions-row {
  display: flex;
  justify-content: flex-end;
}

/* Loading Skeletons */
.loading-state {
  padding: 1rem 0;
}

.skeleton-card {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 1.25rem;
  margin-bottom: 1rem;
}

.skeleton-line {
  height: 20px;
  background: linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 4px;
  margin-bottom: 0.75rem;
}

.skeleton-line.short {
  width: 60%;
}

@keyframes shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

/* Empty State */
.empty-state {
  text-align: center;
  padding: 4rem 2rem;
  background: white;
  border-radius: 8px;
  border: 2px dashed #e2e8f0;
}

.empty-icon {
  font-size: 4rem;
  color: #cbd5e0;
  margin-bottom: 1.5rem;
}

.empty-state h3 {
  font-size: 1.5rem;
  color: #1a202c;
  margin-bottom: 0.75rem;
}

.empty-state p {
  color: #64748b;
  margin-bottom: 1.5rem;
}

/* Pagination */
.pagination-controls {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  background: white;
  border-radius: 8px;
  margin-top: 1.5rem;
}

.pagination-info {
  color: #64748b;
  font-size: 0.9375rem;
}

.pagination-buttons {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.page-numbers {
  display: flex;
  gap: 0.25rem;
  margin: 0 0.5rem;
}

.page-btn {
  padding: 0.5rem 0.75rem;
  border: 1px solid #e2e8f0;
  background: white;
  color: #64748b;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.875rem;
  transition: all 0.2s ease;
}

.page-btn:hover {
  border-color: #667eea;
  color: #667eea;
}

.page-btn.active {
  background: #667eea;
  color: white;
  border-color: #667eea;
}

.page-size-controls label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9375rem;
  color: #64748b;
}

.form-select {
  padding: 0.375rem 0.75rem;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  font-size: 0.875rem;
}

@media (max-width: 768px) {
  .search-filter-row {
    flex-direction: column;
  }
  
  .filter-select {
    width: 100%;
  }
  
  .pagination-controls {
    flex-direction: column;
    gap: 1rem;
  }
  
  .page-numbers {
    flex-wrap: wrap;
  }
}
</style>

